import * as React from "react";
import { Link } from "react-router-dom";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../..";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "../..";
import { ChevronRightIcon } from "lucide-react";
import {
  SidebarNavBadgeVariant,
  getSidebarNavBadgeLabel,
  type SidebarNavBadge,
} from "./nav-badge";
import { cn } from "../../lib/utils";

const navBadgeVariantClassNames: Record<SidebarNavBadgeVariant, string> = {
  [SidebarNavBadgeVariant.Success]:
    "bg-emerald-500/15 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300",
  [SidebarNavBadgeVariant.Warning]:
    "bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300",
  [SidebarNavBadgeVariant.Info]:
    "bg-sky-500/15 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300",
  [SidebarNavBadgeVariant.Neutral]:
    "bg-muted text-muted-foreground dark:bg-muted/60 dark:text-muted-foreground",
};

function getNavBadgeVariant(badge: SidebarNavBadge): SidebarNavBadgeVariant {
  return typeof badge === "string"
    ? SidebarNavBadgeVariant.Warning
    : badge.variant ?? SidebarNavBadgeVariant.Neutral;
}

/**
 * A NUMERIC count on a nav row — "how many things are waiting here".
 *
 * Deliberately not `NavBadge`, which LABELS a row ("Beta", "Soon") and defaults to amber. A pending count is
 * not a warning, and rendering it through the label badge produced a murky amber block against the dark
 * sidebar. This matches the work queue's count treatment, so every "N waiting" in the sidebar reads alike.
 *
 * Red, softened. A 15%-opacity emerald pill sat on a sidebar whose ACTIVE row is also emerald-tinted, so the
 * one thing that should catch the eye was the hardest thing on it to find. Red is the notification
 * convention — it counts what is waiting, it is not a control — but at full strength it shouted over the
 * navigation it belongs to, so it sits just below solid.
 *
 * Sits NEXT TO the label rather than pushed to the far edge: it qualifies that row ("Transfers, 1 waiting"),
 * and stranded against the opposite margin it reads as unrelated furniture. Matching `min-w` and
 * `tabular-nums` keep 1 and 12 the same shape, so the row does not shift as the number changes.
 */
function NavCount({ count, className }: { count: number; className?: string }) {
  return (
    <span
      className={cn(
        "ml-1.5 inline-flex h-[1.375rem] min-w-[1.375rem] shrink-0 items-center justify-center rounded-full px-1.5",
        "text-xs font-semibold leading-none tabular-nums",
        "bg-red-500/80 text-white dark:bg-red-500/70 dark:text-white",
        className,
      )}
    >
      {count}
    </span>
  );
}

function NavBadge({ badge, className }: { badge: SidebarNavBadge; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded-md px-1.5 text-[0.625rem] font-semibold leading-none tracking-wide",
        navBadgeVariantClassNames[getNavBadgeVariant(badge)],
        className,
      )}
    >
      {getSidebarNavBadgeLabel(badge)}
    </span>
  );
}

export function NavMain({
  items,
  label,
}: {
  /** Leave it out for the first group: the list opens on its items, the way the shell is drawn. */
  label?: string;
  items: {
    title: string;
    url: string;
    icon?: React.ReactNode;
    badge?: SidebarNavBadge;
    count?: number;
    dataTour?: string;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
      disabled?: boolean;
      isActive?: boolean;
      badge?: SidebarNavBadge;
      /** Live "waiting on you" count, e.g. transfers pending this branch's approval. */
      count?: number;
      highlight?: boolean;
    }[];
  }[];
}) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const activeParentTitle = items.find((item) => item.isActive)?.title ?? null;
  const [openItemTitle, setOpenItemTitle] = React.useState<string | null>(activeParentTitle);

  React.useEffect(() => {
    setOpenItemTitle(activeParentTitle);
  }, [activeParentTitle]);

  // Never render an empty group header (e.g. the parish sidebar has no "Operations" items).
  if (items.length === 0) {
    return null;
  }

  return (
    <SidebarGroup className="px-3 py-1 group-data-[collapsible=icon]:px-2">
      {label ? (
        <SidebarGroupLabel className="text-sidebar-foreground/50 h-7 px-2.5 pt-2 text-xs font-semibold group-data-[collapsible=icon]:hidden">
          {label}
        </SidebarGroupLabel>
      ) : null}
      <SidebarMenu className="gap-0.5">
        {items.map((item) => (
          item.items && item.items.length > 0 ? (
            isCollapsed ? (
              <SidebarMenuItem key={item.title}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <SidebarMenuButton
                      // `title`, NOT `tooltip`: this button is already a Radix trigger's `asChild` child, and
                      // `SidebarMenuButton` wraps itself in a SECOND trigger (`TooltipTrigger asChild`) whenever
                      // `tooltip` is set. Two Radix slots on one element compose refs as
                      // `composeRefs(tooltipRef, composeRefs(dropdownRef, ...))`; Slot rebuilds that on every
                      // render and both triggers store the node in state, so each render detached and reattached
                      // the ref, dispatched state, and rendered again — "Maximum update depth exceeded", thrown
                      // from inside composeRefs where nothing names the component at fault.
                      title={item.title}
                      isActive={item.isActive}
                      data-tour={item.dataTour}
                      className="h-9 rounded-lg px-2.5 text-[15px] hover:!bg-primary/5 hover:!text-primary data-[active=true]:!bg-primary/10 data-[active=true]:!text-primary data-[active=true]:font-medium dark:hover:!bg-white/5 dark:hover:!text-emerald-300 dark:data-[active=true]:!bg-emerald-400/10 dark:data-[active=true]:!text-emerald-300 [&_svg]:!size-[1.1rem]"
                    >
                      {item.icon}
                      <span>{item.title}</span>
                    </SidebarMenuButton>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="right" sideOffset={10} className="min-w-56 rounded-xl p-1.5">
                    <DropdownMenuLabel className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium text-muted-foreground [&_svg]:!size-[1.1rem]">
                      {item.icon}
                      <span>{item.title}</span>
                      {item.badge ? <NavBadge badge={item.badge} className="ml-1" /> : null}
                    </DropdownMenuLabel>
                    {item.items.map((subItem) => (
                      <DropdownMenuItem
                        key={subItem.title}
                        asChild={!subItem.disabled}
                        disabled={subItem.disabled}
                        className={cn(
                          "gap-2 rounded-lg px-2.5 py-1.5 text-[15px]",
                          subItem.isActive ? "bg-primary/10 text-primary dark:bg-emerald-400/10 dark:text-emerald-300" : undefined,
                        )}
                      >
                        {subItem.disabled ? (
                          <span className="flex min-w-0 items-center">
                            <span className="truncate">{subItem.title}</span>
                            {subItem.badge ? <NavBadge badge={subItem.badge} className="ml-1.5" /> : null}
                          </span>
                        ) : (
                          <Link to={subItem.url}>
                            <span className="truncate">{subItem.title}</span>
                            {subItem.badge ? <NavBadge badge={subItem.badge} className="ml-1.5" /> : null}
                            {subItem.count ? <NavCount count={subItem.count} /> : null}
                          </Link>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </SidebarMenuItem>
            ) : (
              <Collapsible
                key={item.title}
                asChild
                open={openItemTitle === item.title}
                onOpenChange={(open) => setOpenItemTitle(open ? item.title : null)}
                className='group/collapsible'
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      // `title`, NOT `tooltip`: this button is already a Radix trigger's `asChild` child, and
                      // `SidebarMenuButton` wraps itself in a SECOND trigger (`TooltipTrigger asChild`) whenever
                      // `tooltip` is set. Two Radix slots on one element compose refs as
                      // `composeRefs(tooltipRef, composeRefs(dropdownRef, ...))`; Slot rebuilds that on every
                      // render and both triggers store the node in state, so each render detached and reattached
                      // the ref, dispatched state, and rendered again — "Maximum update depth exceeded", thrown
                      // from inside composeRefs where nothing names the component at fault.
                      title={item.title}
                      isActive={item.isActive}
                      data-tour={item.dataTour}
                      className="h-9 rounded-lg px-2.5 text-[15px] hover:!bg-primary/5 hover:!text-primary data-[active=true]:!bg-primary/10 data-[active=true]:!text-primary data-[active=true]:font-medium group-data-[state=open]/collapsible:!bg-primary/10 group-data-[state=open]/collapsible:!text-primary dark:hover:!bg-white/5 dark:hover:!text-emerald-300 dark:data-[active=true]:!bg-emerald-400/10 dark:data-[active=true]:!text-emerald-300 dark:group-data-[state=open]/collapsible:!bg-emerald-400/10 dark:group-data-[state=open]/collapsible:!text-emerald-300 [&_svg]:!size-[1.1rem]"
                    >
                      {item.icon}
                      <span>{item.title}</span>
                      {item.badge ? (
                        <NavBadge badge={item.badge} className="ml-1.5" />
                      ) : null}
                      <ChevronRightIcon className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          {subItem.disabled ? (
                            <SidebarMenuSubButton
                              aria-disabled='true'
                              tabIndex={-1}
                              className='cursor-not-allowed text-[15px] opacity-60'
                            >
                              <span>{subItem.title}</span>
                              {subItem.badge ? <NavBadge badge={subItem.badge} className="ml-1.5" /> : null}
                              {subItem.count ? <NavCount count={subItem.count} /> : null}
                            </SidebarMenuSubButton>
                          ) : (
                            <SidebarMenuSubButton
                              asChild
                              isActive={subItem.isActive}
                              className={cn(
                                "text-[15px] hover:!bg-transparent hover:!text-primary data-[active=true]:!bg-transparent data-[active=true]:!text-primary data-[active=true]:font-medium dark:hover:!text-emerald-300 dark:data-[active=true]:!text-emerald-300",
                                subItem.highlight && "!text-primary font-medium dark:!text-emerald-300",
                              )}
                            >
                              <Link to={subItem.url}>
                                <span>{subItem.title}</span>
                                {subItem.badge ? <NavBadge badge={subItem.badge} className="ml-1.5" /> : null}
                                {subItem.count ? <NavCount count={subItem.count} /> : null}
                              </Link>
                            </SidebarMenuSubButton>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          ) : (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton
                asChild
                tooltip={item.title}
                isActive={item.isActive}
                className="h-9 rounded-lg px-2.5 text-[15px] hover:!bg-primary/5 hover:!text-primary data-[active=true]:!bg-primary/10 data-[active=true]:!text-primary data-[active=true]:font-medium dark:hover:!bg-white/5 dark:hover:!text-emerald-300 dark:data-[active=true]:!bg-emerald-400/10 dark:data-[active=true]:!text-emerald-300 [&_svg]:!size-[1.1rem]"
              >
                <Link to={item.url} data-tour={item.dataTour}>
                  {item.icon}
                  <span>{item.title}</span>
                  {item.badge ? (
                    <NavBadge badge={item.badge} className="ml-1.5" />
                  ) : null}
                  {item.count ? (
                    <SidebarMenuBadge className="bg-red-500/80 font-semibold text-white dark:bg-red-500/70 dark:text-white">
                      {item.count}
                    </SidebarMenuBadge>
                  ) : null}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
